import { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import { products, categories } from "@/mocks/products";

const ProductsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [dropshippingOnly, setDropshippingOnly] = useState(searchParams.get("dropshipping") === "true");
  const [verifiedOnly, setVerifiedOnly] = useState(searchParams.get("verified") === "true");
  const [selectedCountry, setSelectedCountry] = useState(searchParams.get("country") || "");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [keyword, setKeyword] = useState(searchParams.get("q") || "");

  const filtered = useMemo(() => products.filter((p) => {
    if (dropshippingOnly && !p.dropshipping) return false;
    if (verifiedOnly && !p.verified) return false;
    if (selectedCountry && !p.country.toLowerCase().includes(selectedCountry.toLowerCase())) return false;
    if (selectedCategory && !p.category.toLowerCase().includes(selectedCategory.toLowerCase())) return false;
    if (priceMin && p.price.min < Number(priceMin)) return false;
    if (priceMax && p.price.max > Number(priceMax)) return false;
    if (keyword && !p.name.toLowerCase().includes(keyword.toLowerCase()) && !p.supplierName.toLowerCase().includes(keyword.toLowerCase())) return false;
    return true;
  }), [dropshippingOnly, verifiedOnly, selectedCountry, selectedCategory, priceMin, priceMax, keyword]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="Buy Factory Products Direct | Middle East Verified Suppliers | SuperMiddleEast"
        description="Browse 5M+ products from verified factories in Dubai, Saudi Arabia, Qatar, Turkey and Pakistan. Filter by dropshipping, verified supplier, country and category. Sample & bulk orders available."
        keywords="buy factory products, Middle East wholesale, Dubai products, Saudi Arabia factory, verified supplier, dropshipping products, bulk order"
        canonical={`${BASE_URL}/products`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "Factory Products – SuperMiddleEast",
            "description": "Browse products from verified factories across the Middle East, Turkey and Pakistan. Sample orders, bulk orders and dropshipping available.",
            "url": `${BASE_URL}/products`,
            "inLanguage": "en",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
                { "@type": "ListItem", "position": 2, "name": "Products", "item": `${BASE_URL}/products` },
              ],
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "Verified Factory Products",
            "description": "Products from verified Middle East factories available for sample, bulk and dropshipping orders.",
            "url": `${BASE_URL}/products`,
          },
        ]}
      />
      <Navbar />
      <div className="pt-20 max-w-screen-xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>Product Search</h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} products found from verified Middle East factories</p>
        </div>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Filters */}
          <aside className="w-full lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl p-6 border border-amber-100 sticky top-24">
              <h2 className="font-bold text-gray-900 mb-5 flex items-center gap-2"><i className="ri-filter-3-line text-amber-500"></i> Filters</h2>
              {/* Search */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Keyword</label>
                <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search products..." className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
              </div>
              {/* Quick Toggles */}
              <div className="mb-5 space-y-3">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Quick Filters</label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700 flex items-center gap-2"><i className="ri-truck-line text-amber-500"></i> Welcome — Container &amp; Drop Shipping Available</span>
                  <div onClick={() => setDropshippingOnly(!dropshippingOnly)} className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${dropshippingOnly ? "bg-amber-500" : "bg-gray-200"}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${dropshippingOnly ? "left-5" : "left-0.5"}`}></div>
                  </div>
                </label>
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700 flex items-center gap-2"><i className="ri-shield-check-line text-amber-500"></i> Verified Only</span>
                  <div onClick={() => setVerifiedOnly(!verifiedOnly)} className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${verifiedOnly ? "bg-amber-500" : "bg-gray-200"}`}>
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${verifiedOnly ? "left-5" : "left-0.5"}`}></div>
                  </div>
                </label>
              </div>
              {/* Country */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Country</label>
                <select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 cursor-pointer bg-white">
                  <option value="">All Countries</option>
                  <option value="UAE">UAE / Dubai</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Turkey">Turkey</option>
                </select>
              </div>
              {/* Category */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Category</label>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 cursor-pointer bg-white">
                  <option value="">All Categories</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              {/* Price Range */}
              <div className="mb-5">
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Price Range (USD)</label>
                <div className="flex gap-2">
                  <input value={priceMin} onChange={(e) => setPriceMin(e.target.value)} type="number" placeholder="Min" className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                  <input value={priceMax} onChange={(e) => setPriceMax(e.target.value)} type="number" placeholder="Max" className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" />
                </div>
              </div>
              <button onClick={() => { setDropshippingOnly(false); setVerifiedOnly(false); setSelectedCountry(""); setSelectedCategory(""); setPriceMin(""); setPriceMax(""); setKeyword(""); }} className="w-full border border-amber-300 text-amber-600 hover:bg-amber-50 text-sm font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                Clear All Filters
              </button>
            </div>
          </aside>

          {/* Product Grid */}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-5">
              <span className="text-sm text-gray-500">{filtered.length} results</span>
              <select className="border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 cursor-pointer bg-white">
                <option>Sort: Relevance</option>
                <option>Price: Low to High</option>
                <option>Price: High to Low</option>
                <option>Top Rated</option>
                <option>Newest</option>
              </select>
            </div>
            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4"><i className="ri-search-line text-3xl text-amber-400"></i></div>
                <h3 className="font-bold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 text-sm">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-product-shop>
                {filtered.map((product) => (
                  <div key={product.id} onClick={() => navigate(`/products/${product.id}`)} className="bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer group border border-amber-50">
                    <div className="relative h-48 overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 left-3 flex gap-1 flex-wrap">
                        {product.verified && <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1"><i className="ri-shield-check-line"></i> Verified</span>}
                        {product.dropshipping && <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded flex items-center gap-1"><i className="ri-truck-line"></i> Dropship</span>}
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-amber-600 font-medium mb-1 truncate">{product.supplierName} · {product.country}</p>
                      <h3 className="font-semibold text-gray-900 text-sm mb-2 line-clamp-2">{product.name}</h3>
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(5)].map((_, i) => <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>)}
                        <span className="text-xs text-gray-400 ml-1">({product.reviews})</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-base font-bold text-gray-900">${product.price.min}–${product.price.max}</span>
                          <div className="text-xs text-gray-400">MOQ: {product.moq} pcs</div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/products/${product.id}`); }} className="border border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors whitespace-nowrap cursor-pointer">View</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductsPage;
